/* eslint-disable @typescript-eslint/no-unused-expressions */
import { useState } from 'react';
import ExpiredStorage from 'expired-storage';

/**
 * An implement of the same API of react useState but with the difference of
 * localStorage persist. So, you can change your state and when the user reload
 * the browser, the state will persist!
 *
 * @param key name of the key to be stored on localStorage
 * @param initialValue value to be the default, like useState('defaultvalue')
 * @param expire time in seconds to expiry this state, set false to infinite
 */

export function useLocalStorage<T>(
  key: string,
  initialValue: T,
  expire: number | boolean = 60 * 30,
): [T, (value: T) => void] {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      // Instantiate the expiredStorage
      const expiredStorage = new ExpiredStorage();

      // Get from local storage by key
      const item = expiredStorage.getItem(key);

      const parsedInitialValue = JSON.stringify(initialValue);

      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : JSON.parse(parsedInitialValue);
    } catch (error: any) {
      // If error also return initialValue
      console.error(`Error when using useLocalStorage${error}`);

      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: T): void => {
    try {
      const expiredStorage = new ExpiredStorage();

      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;

      // Save state
      setStoredValue(valueToStore);

      // Save to local storage
      expire === false
        ? window.localStorage.setItem(key, JSON.stringify(valueToStore))
        : expiredStorage.setItem(
            key,
            JSON.stringify(valueToStore),
            typeof expire === 'number' ? expire : undefined,
          );
    } catch (error: any) {
      // A more advanced implementation would handle the error case
      console.error(error);
    }
  };

  return [storedValue, setValue];
}

export default useLocalStorage;
